// resources/js/calculator/state.js
// The obrCalc() x-data factory — the single Alpine component for the whole calculator
// (CLAUDE.md §4: "wszystkie ekrany kalkulatora są w jednym komponencie Alpine.js,
// przełączanym przez x-show"). Registered via Alpine.data('obrCalc', obrCalc) in app.js.
//
// NOTE: obrCalc MUST be a plain function (not an arrow) — Alpine.data binds `this` to the
// returned component object and rebinds on each `x-data` instance.
//
// Public surface (03-02/03-03 bind to these; see 03-01-SUMMARY.md):
//   state:   step, turnover, tenure, industry, errors, loadingTextIndex, apiError, result,
//            loading, liveMsg
//   getters: pct, turnoverFormatted
//   methods: init, next, back, goto, submit, selectTenure, selectIndustry, moveTenure,
//            moveIndustry, onTurnoverInput, clampTurnover, persist, syncUrl, focusPanel,
//            focusError, clampToReachable, turnoverValid
//   arrays:  tenureOrder, industryOrder, loaderTexts
//
// Phase 6 (conversion path) — LOCKED contract Plans 07/08/09 bind to WITHOUT editing this file:
//   state:   selectedTileIndex, selectRequiredError, phoneDigits, consentCall, consentSms,
//            website, formShownAt, submitting, leadError, leadResponse, submitted
//   getters: phoneFormatted
//   methods: onPhoneInput, selectTile, goToForm, backToResult, submitLead

import { validateStep, validateForm } from './validation.js'
import { calculate, submitLead } from './api.js'
import { track } from './analytics.js'
import { getUtm } from '../utm.js'

const STORAGE_KEY = 'obr_calc_state'
const TTL_MS = 7 * 864e5 // 7 days (CONTEXT D-06)

export function obrCalc() {
  return {
    // ── navigation ───────────────────────────────────────────────────────────────
    step: 1, // 1 | 2 | 3 | 'loading' | 'result' | 'form' | 'confirmed'
    maxStep: 3,
    loading: false,

    // ── inputs (CLAUDE.md §5) ─────────────────────────────────────────────────────
    turnover: 47000, // PLN integer, slider+input synced
    tenure: null, // '0_3' | '3_6' | '6_12' | '12_plus'
    industry: null, // 'restauracja' | 'sklep' | 'hotel' | 'uslugi'

    // ── ui state ──────────────────────────────────────────────────────────────────
    errors: {}, // { turnover?, tenure?, industry? }
    loadingTextIndex: 0,
    apiError: null,
    result: null,
    liveMsg: '',
    rootEl: null, // korzeń komponentu zapamiętany w init() — patrz komentarz tamże

    // ── conversion path (Phase 6: form + confirmation) ────────────────────────────
    selectedTileIndex: null, // 0-based tile choice on standard/high result — the ONLY client selection signal (D-03; no offer ids client-side)
    selectRequiredError: false, // drives the D-01 required-select error slot + tile --error borders (Plan 09 binds)
    phoneDigits: '', // raw 9-digit national number (model); server prepends +48
    consentCall: false, // required consent (LEAD-02); never pre-checked
    consentSms: false, // optional consent
    website: '', // honeypot — present AND empty for a human (LEAD-03)
    formShownAt: null, // Date.now() at form mount — feeds elapsed_ms time-trap (Pitfall 7)
    submitting: false, // single-flight guard for the submit button (LEAD-04)
    leadError: null, // one-slot message: client-invalid OR D-07 server error
    leadResponse: null, // /lead 200 body { status, within_working_hours, delivered, track }
    submitted: false, // set BEFORE step→'confirmed' so persist() is gated after the storage clear (Pitfall 3 / D-11)

    // ── slider bounds ─────────────────────────────────────────────────────────────
    min: 10000,
    max: 2000000,
    snap: 1000,

    // ── traversal order arrays ────────────────────────────────────────────────────
    tenureOrder: ['0_3', '3_6', '6_12', '12_plus'],
    industryOrder: ['restauracja', 'sklep', 'hotel', 'uslugi'],
    loaderTexts: [
      'Może to potrwać kilka chwil. Sprawdzamy podstawowe parametry, aby przygotować wstępny wynik finansowania.',
      'Dopasowujemy dostępne opcje finansowania…',
      'Przygotowujemy Twój wstępny wynik…',
    ],

    // ── getters ───────────────────────────────────────────────────────────────────
    // Skala SEGMENTOWA (PNG: podziałki 10 tys. / 250 tys. / 500 tys. / 2 mln rozłożone
    // równomiernie co 1/3 osi) — liniowe mapowanie ściskało 10k-250k do 12% suwaka i przy
    // 47 000 zł knob wyglądał jak 0%. scaleStops trzyma granice segmentów.
    scaleStops: [10000, 250000, 500000, 2000000],
    get pct() {
      const s = this.scaleStops
      const v = Math.min(this.max, Math.max(this.min, this.turnover))
      for (let i = 0; i < s.length - 1; i++) {
        if (v <= s[i + 1]) {
          return ((i + (v - s[i]) / (s[i + 1] - s[i])) / (s.length - 1)) * 100
        }
      }
      return 100
    },
    // pozycja suwaka 0-1000 ↔ wartość PLN (odwrotność pct; range input jest pozycyjny,
    // żeby drag był spójny wizualnie z segmentową skalą)
    get sliderPos() {
      return Math.round(this.pct * 10)
    },
    setFromSlider(pos) {
      const s = this.scaleStops
      const t = Math.min(1000, Math.max(0, Number(pos))) / 1000 * (s.length - 1)
      const i = Math.min(s.length - 2, Math.floor(t))
      const v = s[i] + (t - i) * (s[i + 1] - s[i])
      this.turnover = Math.round(v / this.snap) * this.snap
    },
    get turnoverFormatted() {
      return new Intl.NumberFormat('pl-PL').format(this.turnover)
    },
    get phoneFormatted() {
      // render "XXX XXX XXX" while the model keeps raw digits (same parse-on-input idiom as turnover)
      return this.phoneDigits
        .replace(/\D/g, '')
        .slice(0, 9)
        .replace(/(\d{3})(?=\d)/g, '$1 ')
        .trim()
    },

    // ── lifecycle ─────────────────────────────────────────────────────────────────
    init() {
      // 0. Zapamiętany korzeń komponentu.
      //
      // ⛔ NIE wolno czytać `this.$root` w odroczonym wywołaniu (`$nextTick` / rAF). Alpine
      // ustala tę magiczną właściwość OD ELEMENTU, KTÓRY ODPALIŁ AKCJĘ, a przyciski ekranów
      // przejmujących stoją wewnątrz `<template x-if>`. Gdy krok się zmienia, Alpine usuwa
      // zawartość szablonu razem z tym przyciskiem — odroczone wywołanie pyta wtedy o korzeń
      // przez element odpięty od dokumentu i dostaje `undefined`.
      //
      // ZMIERZONE 2026-08-16 (bramka fazy 11): po udanym wysłaniu numeru leciał
      // `TypeError: Cannot read properties of undefined (reading 'querySelector')`
      // (`focusPanel`), a focus zamiast na nagłówku „Zgłoszenie przyjęte" (który ma
      // przygotowany `tabindex="-1"`) lądował na `<body>`. Ekrany wyniku i formularza
      // działały, bo tam akcję odpalał element spoza usuwanego szablonu.
      //
      // Wada jest ZASTANA — ten sam zapis stoi w kodzie sprzed fazy 11 (commit `4a0d08d`),
      // migracja ruszyła w tym pliku wyłącznie nazwy selektorów.
      this.rootEl = this.$root

      // 1. restore from localStorage (clamp + validate + TTL)
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
        const fresh = typeof saved.savedAt === 'number' && Date.now() - saved.savedAt <= TTL_MS
        if (fresh) {
          if (typeof saved.turnover === 'number') {
            this.turnover = Math.min(
              this.max,
              Math.max(this.min, Math.round(saved.turnover / this.snap) * this.snap)
            )
          }
          if (this.tenureOrder.includes(saved.tenure)) this.tenure = saved.tenure
          if (this.industryOrder.includes(saved.industry)) this.industry = saved.industry
          if ([1, 2, 3].includes(saved.step)) this.step = saved.step
        } else if (saved.savedAt) {
          // stale → discard
          localStorage.removeItem(STORAGE_KEY)
        }
      } catch (_) {
        /* corrupt storage → keep defaults */
      }

      // 2. deep-link ?step=N overrides (accept only 1-3), then clamp to first reachable
      const q = parseInt(new URLSearchParams(location.search).get('step'), 10)
      if ([1, 2, 3].includes(q)) this.step = q
      this.step = this.clampToReachable(this.step)

      // 3. keep storage + URL in sync
      this.$watch('turnover', () => this.persist())
      this.$watch('tenure', () => this.persist())
      this.$watch('industry', () => this.persist())
      this.$watch('step', () => {
        this.persist()
        this.syncUrl()
        // broadcast for out-of-scope UI (nav switches to its minimal variant during 'loading')
        window.dispatchEvent(new CustomEvent('obr:step', { detail: this.step }))
      })

      // 4. back/forward re-reads ?step
      window.addEventListener('popstate', () => {
        const s = parseInt(new URLSearchParams(location.search).get('step'), 10)
        if ([1, 2, 3].includes(s)) this.step = this.clampToReachable(s)
      })

      track('calc_started')
      this.syncUrl()
      this.focusPanel()
    },

    // ── validation helpers ────────────────────────────────────────────────────────
    turnoverValid() {
      return this.turnover >= this.min && this.turnover <= this.max
    },
    clampToReachable(target) {
      if (target >= 2 && !this.turnoverValid()) return 1
      if (target >= 3 && this.tenure === null) return 2
      return target
    },

    // ── navigation methods ────────────────────────────────────────────────────────
    next() {
      const e = validateStep(this.step, this)
      if (Object.keys(e).length) {
        this.errors = e
        this.focusError()
        return
      }
      this.errors = {}
      track('calc_step_completed', { step_number: this.step })
      if (this.step === 3) return this.submit()
      this.step++
      this.focusPanel()
    },
    back() {
      if (this.step > 1) {
        this.step--
        this.errors = {}
        this.focusPanel()
      }
    },
    goto(n) {
      this.step = this.clampToReachable(n)
      this.focusPanel()
    },

    // ── radio selection ───────────────────────────────────────────────────────────
    selectTenure(v) {
      this.tenure = v
      this.errors.tenure = null
    },
    selectIndustry(v) {
      this.industry = v
      this.errors.industry = null
    },
    // Roving focus po strzałce: scope tego komponentu to CAŁE hero, więc obie grupy kroków
    // (2 i 3) żyją w nim naraz i KAŻDA ma własny kafel z tabindex="0". Grupa poprzedniego
    // kroku stoi wcześniej w DOM, więc samo querySelector zwracało kafel ukryty (display:none),
    // a focus() na nim jest no-opem — w kroku 3 focus zostawał na kaflu sprzed zaznaczenia
    // (zmierzone, docs/plans/baseline/reports/mig-01-klawiatura.txt). Bierzemy pierwszy kafel,
    // który jest FAKTYCZNIE wyrenderowany; filtr po geometrii przeżyje każdą zmianę nazw klas.
    moveTenure(dir) {
      // When nothing is selected yet, roving focus sits on the first card (tabindex=0), so an
      // arrow must advance FROM index 0 (→ card 0+dir), not snap-select card 0 (APG radiogroup:
      // from the focused radio, Arrow moves focus+selection to the adjacent one).
      const cur = this.tenureOrder.indexOf(this.tenure)
      const base = cur === -1 ? 0 : cur
      const next = (base + dir + this.tenureOrder.length) % this.tenureOrder.length
      this.selectTenure(this.tenureOrder[next])
      this.$nextTick(() => Array.from((this.rootEl || this.$root)?.querySelectorAll('[data-calc-tab][tabindex="0"]') || []).find((t) => t.getClientRects().length)?.focus())
    },
    moveIndustry(dir) {
      const cur = this.industryOrder.indexOf(this.industry)
      const base = cur === -1 ? 0 : cur
      const next = (base + dir + this.industryOrder.length) % this.industryOrder.length
      this.selectIndustry(this.industryOrder[next])
      this.$nextTick(() => Array.from((this.rootEl || this.$root)?.querySelectorAll('[data-calc-tab][tabindex="0"]') || []).find((t) => t.getClientRects().length)?.focus())
    },

    // ── turnover input (parse-on-input, reformat-on-blur — RESEARCH Pitfall 2) ──────
    onTurnoverInput(e) {
      const digits = e.target.value.replace(/\D/g, '')
      let v = digits ? parseInt(digits, 10) : 0
      // cap magnitude live so the field can never show an absurd value (e.g. pasted/typed
      // 18 digits). Snapping to `snap` + enforcing `min` still happens on blur (clampTurnover).
      if (v > this.max) v = this.max
      this.turnover = v
      this.errors.turnover = null
    },
    clampTurnover() {
      let v = Math.round((this.turnover || 0) / this.snap) * this.snap
      v = Math.min(this.max, Math.max(this.min, v))
      this.turnover = v
    },

    // ── submit / loader theatre (CALC-06) ─────────────────────────────────────────
    async submit() {
      this.errors = {}
      this.apiError = null
      this.step = 'loading'
      this.loading = true
      this.loadingTextIndex = 0
      this.focusPanel()

      const rm = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const rotate = rm
        ? null
        : setInterval(() => {
            this.loadingTextIndex = (this.loadingTextIndex + 1) % this.loaderTexts.length
          }, 900) // 900ms per text — all 3 rotate within the 2.8s floor, each readable

      try {
        const [res] = await Promise.all([
          calculate({
            avg_monthly_turnover_pln: this.turnover,
            tenure_band: this.tenure,
            industry_vertical: this.industry,
          }),
          new Promise((r) => setTimeout(r, 2800)), // min ~2.8s loader floor — 1.2s felt rushed (user 2026-08-09); fits ~2 hex swaps + full text rotation
        ])
        this.result = res
        const viewParams = {
          eligible_count: res.eligible_count,
          displayed_count: res.displayed_count,
          result_type: res.result_type,
        }
        if (res.result_type === 'no_match') {
          track('calc_no_match_viewed', viewParams) // fallback impression (CLAUDE.md §8, ANL-01)
        } else {
          track('calc_result_viewed', viewParams) // qualified-result impression
        }
        this.$dispatch('calc:result-ready', res) // Phase-5 result screen listens here (CONTEXT D-12)
        this.step = 'result' // throwaway placeholder panel this phase
        this.focusPanel()
      } catch (_) {
        this.apiError = 'Coś poszło nie tak. Spróbuj ponownie za chwilę.'
        this.step = 3 // back to step 3 with retry (CONTEXT D-04)
        this.focusPanel()
      } finally {
        if (rotate) clearInterval(rotate)
        this.loading = false
      }
    },

    // ── conversion path: tile-select + phone form + lead submit (Phase 6) ──────────
    onPhoneInput(e) {
      // parse-on-input: strip non-digits, cap 9; model holds raw digits (phoneFormatted renders groups)
      this.phoneDigits = e.target.value.replace(/\D/g, '').slice(0, 9)
      this.leadError = null
    },
    selectTile(index) {
      // single-select toggle: clicking the selected tile deselects (selection required only at CTA time, D-01)
      this.selectedTileIndex = this.selectedTileIndex === index ? null : index
      // selection ALWAYS clears the D-01 group error ("selected beats error")
      this.selectRequiredError = false
    },
    goToForm() {
      if (this.selectedTileIndex === null) {
        // stay on 'result'; Plan 09 Blade renders the reserved-slot error + tile --error borders off this flag
        this.selectRequiredError = true
        return
      }
      this.selectRequiredError = false
      this.step = 'form'
      this.formShownAt = Date.now() // time-trap baseline starts at form mount (Pitfall 7)
      this.focusPanel()
    },
    backToResult() {
      // additive D-04 "Wstecz": return to the result; result + selection kept in Alpine memory
      this.step = 'result'
      this.errors = {}
      this.focusPanel()
    },
    async submitLead() {
      // NOTE: the bare `submitLead(...)` call below resolves to the api.js import (lexical scope),
      // NOT this method — no recursion. `this.submitLead()` is the method; `submitLead()` is the POST.
      const e = validateForm(this)
      if (Object.keys(e).length) {
        const field = Object.keys(e)[0]
        this.leadError = e[field]
        track('form_error', { field_name: field, error_type: 'validation' })
        this.focusError()
        return
      }
      this.submitting = true
      this.leadError = null
      try {
        const res = await submitLead({
          phone: this.phoneDigits,
          consent_call: this.consentCall,
          consent_sms: this.consentSms,
          avg_monthly_turnover_pln: this.turnover,
          tenure_band: this.tenure,
          industry_vertical: this.industry,
          selected_option_index: this.selectedTileIndex, // null for the no_match fallback form
          elapsed_ms: Date.now() - this.formShownAt,
          website: this.website,
          page_url: location.href,
          // Zrodlo wejscia z PAMIECI strony (D-33). Bez zgody marketingowej cookie nie istnieje,
          // wiec to jedyna droga, ktora dziala zawsze — serwer bierze te wartosc przed cookie.
          utm: getUtm(),
        })
        this.leadResponse = res
        if (res.track === true) {
          // GA4 conversion — gated by the server flag (bot/duplicate → track:false → no push, D-09)
          track('lead_phone_submitted', {
            consent_call: this.consentCall,
            consent_sms: this.consentSms,
          })
        }
        track(res.delivered ? 'webhook_success' : 'webhook_error')
        // MUST precede the step change: the $watch('step')→'confirmed' fires persist(), which the
        // submitted flag now gates so it can't re-write inputs after the clear (Pitfall 3 / D-11).
        this.submitted = true
        this.step = 'confirmed'
        localStorage.removeItem(STORAGE_KEY)
        this.focusPanel()
      } catch (_) {
        // D-07: stay on the current step, inputs + consents preserved, button re-enabled
        this.leadError = 'Coś poszło nie tak. Spróbuj ponownie.'
      } finally {
        this.submitting = false
      }
    },

    // ── persistence (CALC-05) ──────────────────────────────────────────────────────
    persist() {
      // Pitfall 3 / D-11: after a successful submit the storage is cleared and step → 'confirmed';
      // the $watch('step') MUST NOT re-persist stale turnover/tenure/industry over the fresh state.
      if (this.submitted) return
      // only persist numeric steps 1-3; keep the last numeric step for loading/result
      const numericStep = [1, 2, 3].includes(this.step) ? this.step : undefined
      const payload = {
        turnover: this.turnover,
        tenure: this.tenure,
        industry: this.industry,
        savedAt: Date.now(),
      }
      if (numericStep !== undefined) payload.step = numericStep
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    },
    syncUrl() {
      if (![1, 2, 3].includes(this.step)) return // loader/result not deep-linkable
      const url = new URL(location.href)
      // step 1 keeps the URL clean (no ?step=1 on first view — GA4 page_location
      // fragmentation + pointless share links); deep-linking stays for steps 2-3
      if (this.step === 1) url.searchParams.delete('step')
      else url.searchParams.set('step', String(this.step))
      history.replaceState({}, '', url) // replaceState: never pollute history per step
    },

    // ── focus management (CALC-07) ─────────────────────────────────────────────────
    focusPanel() {
      // Defer one extra frame after $nextTick: when the step JUST changed, the new panel's
      // x-show display + `.is-current` class aren't committed yet inside a single $nextTick,
      // so heading.focus() would no-op on a still-hidden element (activeElement stays <body>).
      // rAF guarantees layout/visibility is applied before we move focus (CALC-07/D-09).
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const root = this.rootEl || this.$root
          if (!root) return
          const heading =
            root.querySelector('[data-panel-heading].is-current') ||
            root.querySelector('[data-panel-heading][data-step="' + this.step + '"]') ||
            root.querySelector('[data-panel-heading]')
          heading?.focus()
        })
      })
      if ([1, 2, 3].includes(this.step)) {
        this.liveMsg = 'Krok ' + this.step + ' z 3'
      }
    },
    focusError() {
      this.$nextTick(() => {
        const root = this.rootEl || this.$root
        if (!root) return
        const control =
          root.querySelector('[aria-invalid="true"]') ||
          root.querySelector('[data-calc-error]')
        control?.focus?.()
      })
    },
  }
}
